import { FC, useEffect, useRef, useState } from "react";
import { BaselineDialogProps, FormType } from "../../common";
import { DialogContainer } from "../../CommonDataParts/DialogContainer2";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { useFormContext } from "react-hook-form";
import { DairyIntervention } from "../../../../models/Intervention";
import { DairyV2Baseline, DairyV2DairyCowStageData } from "../../../../models/Baseline/DairyV2Baseline";
import DsmButtonControlGroup from "../../../helpers/DsmButtonControlGroup";
import { cloneDeep } from "lodash";
import { DsmGrid } from "@dsm-dcs/design-system-react";
import DairyV2OutputCowMilkComponent from "./DairyV2OutputCowMilkComponent";
import DairyV2OutputCowNewbornCalvesComponent from "./DairyV2OutputCowNewbornCalvesComponent";
import DairyV2OutputCowCowsComponent from "./DairyV2OutputCowCowsComponent";
import { DairyV2OutputDairyCow } from "../../../../models/Intervention/DairyV2Intervention";

interface DairyV2OutputCowDialogProps extends BaselineDialogProps {
  formType: FormType;
  stageIndex: number;
  baseline: DairyV2Baseline;
  handleCancel: (param?: string | null) => void;
}

const DairyV2OutputCowDialog: FC<DairyV2OutputCowDialogProps> = ({
  formType = FormType.Add,
  stageIndex = 0,
  baseline,
  handleCancel,
}) => {
  const classes: CSSClassesList = processAndStageStylesV2();
  const intl = useIntl();
  const currResetValue = useRef<DairyV2OutputDairyCow>();
  const fc = useFormContext<DairyIntervention>();

  const [isMilkEdit, setIsMilkEdit] = useState(false);
  const [isCowsEdit, setIsCowsEdit] = useState(false);
  const [isNewbornCalvesEdit, setIsNewbornCalvesEdit] = useState(false);
  const isOutputEdit = isMilkEdit || isCowsEdit || isNewbornCalvesEdit;

  const formTitle = intl.formatMessage({ id: 'SUSTELL.PROCESS.DIALOG.STAGE.OUTPUT.TITLE', });
  const fieldItemPrefix = `stages.${stageIndex}.stageData.output`;

  const currentStageData = baseline.stages[stageIndex].stageData;
  const dairyV2CowOutputData = (currentStageData as DairyV2DairyCowStageData).output;

  useEffect(() => {
    currResetValue.current = cloneDeep(fc.getValues(fieldItemPrefix));
  }, []);

  const cowMilkCancelHandler = (isInnerCancel = true) => {
    if (currResetValue.current?.yearlyMilkProduction === undefined)
      fc.setValue(`${fieldItemPrefix}.yearlyMilkProduction`, "");

    if (currResetValue.current?.averageMilkFat === undefined)
      fc.setValue(`${fieldItemPrefix}.averageMilkFat`, "");

    if (currResetValue.current?.averageMilkProtein === undefined)
      fc.setValue(`${fieldItemPrefix}.averageMilkProtein`, "");

    if (isInnerCancel) {
      fc.setValue(`${fieldItemPrefix}.yearlyMilkProduction`, currResetValue.current?.yearlyMilkProduction);
      fc.setValue(`${fieldItemPrefix}.averageMilkFat`, currResetValue.current?.averageMilkFat);
      fc.setValue(`${fieldItemPrefix}.averageMilkProtein`, currResetValue.current?.averageMilkProtein);
    }

    setIsMilkEdit(false);
  };

  const cowsCancelHandler = (isInnerCancel = true) => {
    if (currResetValue.current?.cowsToAnotherStage === undefined)
      fc.setValue(`${fieldItemPrefix}.cowsToAnotherStage`, "");

    if (currResetValue.current?.averageWeightOfCowsLeavingTheStage === undefined)
      fc.setValue(`${fieldItemPrefix}.averageWeightOfCowsLeavingTheStage`, "");

    if (currResetValue.current?.cowsSold === undefined)
      fc.setValue(`${fieldItemPrefix}.cowsSold`, "");

    if (currResetValue.current?.numberOfMortalitiesCows === undefined)
      fc.setValue(`${fieldItemPrefix}.numberOfMortalitiesCows`, "");

    if (currResetValue.current?.averageWeightAtMortalityCows === undefined)
      fc.setValue(`${fieldItemPrefix}.averageWeightAtMortalityCows`, "");

    if (isInnerCancel) {
      fc.setValue(`${fieldItemPrefix}.cowsToAnotherStage`, currResetValue.current?.cowsToAnotherStage);
      fc.setValue(`${fieldItemPrefix}.averageWeightOfCowsLeavingTheStage`, currResetValue.current?.averageWeightOfCowsLeavingTheStage);
      fc.setValue(`${fieldItemPrefix}.cowsSold`, currResetValue.current?.cowsSold);
      fc.setValue(`${fieldItemPrefix}.numberOfMortalitiesCows`, currResetValue.current?.numberOfMortalitiesCows);
      fc.setValue(`${fieldItemPrefix}.averageWeightAtMortalityCows`, currResetValue.current?.averageWeightAtMortalityCows);
    }

    setIsCowsEdit(false);
  };

  const newbornCancelHandler = (isInnerCancel = true) => {
    if (currResetValue.current?.weanedCalvesToAnotherStage === undefined)
      fc.setValue(`${fieldItemPrefix}.weanedCalvesToAnotherStage`, "");

    if (currResetValue.current?.weanedCalvesSold === undefined)
      fc.setValue(`${fieldItemPrefix}.weanedCalvesSold`, "");

    if (currResetValue.current?.averageWeightAtBirth === undefined)
      fc.setValue(`${fieldItemPrefix}.averageWeightAtBirth`, "");

    if (isInnerCancel) {
      fc.setValue(`${fieldItemPrefix}.weanedCalvesToAnotherStage`, currResetValue.current?.weanedCalvesToAnotherStage);
      fc.setValue(`${fieldItemPrefix}.weanedCalvesSold`, currResetValue.current?.weanedCalvesSold);
      fc.setValue(`${fieldItemPrefix}.averageWeightAtBirth`, currResetValue.current?.averageWeightAtBirth);
    }

    setIsNewbornCalvesEdit(false);
  };

  const handleDialogCancelClick = () => {
    cowMilkCancelHandler(false);
    cowsCancelHandler(false);
    newbornCancelHandler(false);

    fc.setValue(`${fieldItemPrefix}`, currResetValue.current);

    if (handleCancel) {
      handleCancel();
    }
  };

  return (
    <DialogContainer
      formVisible={true}
      handleClose={handleDialogCancelClick}
      iconCode="arrows/arrow-circle-broken-right"
      formTitle={formTitle}
      datasetType="intervention"
      width={isOutputEdit ? "1168px" : "812px"}
    >

      <p className={classes.headingTitleVariant}>
        {intl.formatMessage({ id: "SUSTELL.STAGE.DAIRY.OUTPUT.MILK" })}
      </p>
      <DairyV2OutputCowMilkComponent
        formType={formType}
        fieldItemPrefix={fieldItemPrefix}
        currentStageData={dairyV2CowOutputData}
        isMilkEdit={isMilkEdit}
        setIsMilkEdit={setIsMilkEdit}
        resetEdit={cowMilkCancelHandler}
      />

      <DsmGrid className={classes.dsmGridTwoColumnNoRowSpacing}>
        <div>
          <p className={classes.headingTitleVariant}>
            {intl.formatMessage({ id: "SUSTELL.STAGE.DAIRY.OUTPUT.COWS" })}
          </p>
          <DairyV2OutputCowCowsComponent
            formType={formType}
            fieldItemPrefix={fieldItemPrefix}
            currentStageData={dairyV2CowOutputData}
            isCowsEdit={isCowsEdit}
            setIsCowsEdit={setIsCowsEdit}
            resetEdit={cowsCancelHandler}
          />
        </div>

        <div>
          <p className={classes.headingTitleVariant}>
            {intl.formatMessage({ id: "SUSTELL.STAGE.DAIRY.OUTPUT.CALVES" })}
          </p>
          <DairyV2OutputCowNewbornCalvesComponent
            formType={formType}
            fieldItemPrefix={fieldItemPrefix}
            currentStageData={dairyV2CowOutputData}
            isNewbornCalvesEdit={isNewbornCalvesEdit}
            setIsNewbornCalvesEdit={setIsNewbornCalvesEdit}
            resetEdit={newbornCancelHandler}
          />
        </div>
      </DsmGrid>

      <DsmButtonControlGroup
        cancelHandler={handleDialogCancelClick}
        saveHandler={handleCancel}
        saveLabel={intl.formatMessage({ id: 'GENERAL.CONFIRM' })}
      />
    </DialogContainer>
  )
}

export default DairyV2OutputCowDialog;
